import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, Dialog,
  DialogActions, DialogContent, DialogTitle, Stack, Typography,
} from '@mui/material';
import Iconify from '../../../components/iconify';
import { getPresetPacks, installPresetPack } from '../../../services/violationService';

/**
 * Installs a prebuilt rule pack.
 *
 * Every threshold shows its rationale and its source, and a pack's numbers are
 * labelled by where they come from. A customer told their air is "Poor" will ask
 * why, and a VPSi default must not be mistaken for a regulatory limit.
 */

PresetPickerDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onInstalled: PropTypes.func.isRequired,
};

const SOURCE_LABELS = {
  regulatory: { label: 'Regulatory limit', color: 'error' },
  guideline: { label: 'Published guideline', color: 'warning' },
  vendor: { label: 'Vendor scale', color: 'info' },
  'vpsi-default': { label: 'VPSi default — tune to your site', color: 'default' },
};

function PresetPickerDialog({ open, onClose, onInstalled }) {
  const [packs, setPacks] = useState([]);
  const [installing, setInstalling] = useState(null);
  const [error, setError] = useState(null);
  const [installed, setInstalled] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setInstalled(null);
    getPresetPacks()
      .then(setPacks)
      .catch(() => setError('Could not load the prebuilt packs.'));
  }, [open]);

  const install = async (packId) => {
    setInstalling(packId);
    setError(null);
    try {
      const created = await installPresetPack(packId);
      // Re-installing is deliberately a no-op rather than a duplicate, so say so
      // instead of silently appearing to do nothing.
      setInstalled(
        created.length === 0
          ? 'You already have every rule in that pack — nothing was changed.'
          : `Added ${created.length} rule${created.length === 1 ? '' : 's'}.`
      );
      await onInstalled();
    } catch (e) {
      setError('Could not install that pack.');
    } finally {
      setInstalling(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Prebuilt rule packs</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Installing a pack creates the rules as your own, so you can edit any threshold afterwards.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {installed && <Alert severity="success" sx={{ mb: 2 }}>{installed}</Alert>}

        {packs.map((pack) => (
          <Accordion key={pack.id} disableGutters>
            <AccordionSummary expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}>
              <Stack>
                <Typography variant="subtitle1">{pack.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {pack.description}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                {pack.rules.map((rule) => {
                  const source = SOURCE_LABELS[rule.sourceType] ?? SOURCE_LABELS['vpsi-default'];
                  return (
                    <Box key={rule.name}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="subtitle2">{rule.name}</Typography>
                        <Chip size="small" label={source.label} color={source.color} variant="outlined" />
                      </Stack>
                      <Box
                        component="code"
                        sx={{
                          display: 'block', fontFamily: 'monospace', fontSize: 12, my: 0.5,
                          p: 0.75, bgcolor: 'action.hover', borderRadius: 0.5, wordBreak: 'break-word',
                        }}
                      >
                        {rule.expression}
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {rule.rationale}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" display="block">
                        Source: {rule.source}
                      </Typography>
                    </Box>
                  );
                })}
                <Box>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={installing === pack.id}
                    onClick={() => install(pack.id)}
                  >
                    {installing === pack.id ? 'Adding…' : `Add ${pack.name}`}
                  </Button>
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default PresetPickerDialog;
